import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Image, Clock, MoreHorizontal, QrCode, Plus, Eye, ArrowUpRight, Trash2, Users, MapPin, ChevronRight, Loader2 } from 'lucide-react';
import QRCodeModal from '../../../components/modals/QRCodeModal';
import Button from '../../../components/ui/Button';
import { useGetEventsQuery, useDeleteEventMutation } from '../../../store/services/api';
import { Event } from '../../../types/event';

const EventList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showDropdown, setShowDropdown] = useState<string | null>(null);
  
  // RTK Query hooks
  const { data: events = [], isLoading, refetch } = useGetEventsQuery();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const [deletingEventIds, setDeletingEventIds] = useState<{[key: string]: boolean}>({});

  // Etkinlik silme
  const handleDeleteEvent = async (event: Event, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(null);

    if (!window.confirm(`Are you sure you want to delete "${event.title}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingEventIds(prev => ({ ...prev, [event.id]: true }));
    
    try {
      await deleteEvent(event.url);
    } catch (error) {
      // Error handling is done by RTK Query
    } finally {
      setDeletingEventIds(prev => ({ ...prev, [event.id]: false }));
    }
  };

  const filteredEvents = events ? events.filter(event => {
    // Search by title
    return event.title.toLowerCase().includes(searchQuery.toLowerCase());
  }) : [];

  const handleQRCodeClick = (event: Event, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedEvent(event);
  };

  const toggleDropdown = (id: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(showDropdown === id.toString() ? null : id.toString());
  };
  
  // Format date to more readable format
  const formatDate = (dateString: string) => {
    try {
      const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (e) {
      return dateString;
    }
  };
  
  // Calculate time left
  const calculateTimeLeft = (expiresAt: string) => {
    const now = new Date();
    const expiryDate = new Date(expiresAt);
    const diffTime = expiryDate.getTime() - now.getTime();
    
    if (diffTime <= 0) {
      return "Expired";
    }
    
    // Milliseconds to days
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return "1 day left";
    } else if (diffDays < 30) {
      return `${diffDays} days left`;
    } else {
      // If more than 30 days, show months
      const months = Math.floor(diffDays / 30);
      return `${months} ${months === 1 ? 'month' : 'months'} left`;
    }
  };

  return (
    <div className="space-y-3 sm:space-y-6 px-2 sm:px-0">
      {/* Header Section */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 mb-3 sm:mb-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold mb-1">Events</h1>
            <p className="text-xs sm:text-sm text-gray-500">Manage your photo collections</p>
          </div>
          <Link to="/dashboard/events/new">
            <Button className="whitespace-nowrap h-8 sm:h-9 text-xs sm:text-sm px-3 sm:px-4">
              <Plus className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
              New Event
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters and Search Row */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
        {/* Search Bar */}
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-9 py-2 sm:py-2.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#FF4444] focus:border-transparent text-xs sm:text-sm bg-white"
          />
          <Search className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 sm:h-4 w-3.5 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Events List */}
      <div className="space-y-2 sm:space-y-3">
        {isLoading ? (
          <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-100 text-center">
            <Loader2 className="w-6 sm:w-8 h-6 sm:h-8 text-[#FF4444] mx-auto animate-spin mb-3 sm:mb-4" />
            <h3 className="text-sm sm:text-base font-medium mb-1">Loading events...</h3>
            <p className="text-xs sm:text-sm text-gray-500">Please wait while we fetch your data.</p>
          </div>
        ) : filteredEvents.length > 0 ? (
          filteredEvents.map((event) => (
            <div key={event.id} className="relative">
              <Link 
                to={`/dashboard/events/${event.url}`}
                className="block bg-white rounded-lg border border-gray-100 hover:border-[#FF4444]/30 transition-colors overflow-hidden hover:shadow-sm"
              >
                <div className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                  {/* Event Title & Location */}
                  <div className="flex-grow">
                    <h3 className="font-medium text-sm sm:text-base">{event.title}</h3>
                    <div className="flex items-center mt-1 text-xs text-gray-500">
                      <MapPin className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-gray-400 mr-1" />
                      <span>{event.location}</span>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
                    <div className="flex items-center">
                      <Image className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-400 mr-1 sm:mr-1.5" />
                      <span className="text-xs sm:text-sm font-medium">{event.photo_count}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <Clock className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-400 mr-1 sm:mr-1.5" />
                      <span className="text-xs sm:text-sm">{calculateTimeLeft(event.expires_at)}</span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 sm:gap-2 ml-auto sm:ml-0">
                    <button 
                      onClick={(e) => handleQRCodeClick(event, e)}
                      className="p-1 sm:p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                      title="Show QR Code"
                    >
                      <QrCode className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-500" />
                    </button>
                    <button 
                      onClick={(e) => toggleDropdown(event.id, e)}
                      className="p-1 sm:p-1.5 rounded-md hover:bg-gray-100 transition-colors relative"
                      title="More options"
                    >
                      <MoreHorizontal className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-500" />
                    </button>
                    <button className="p-1 sm:p-1.5 rounded-md hover:bg-gray-100 transition-colors" title="View event">
                      <ChevronRight className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              </Link>
              
              {/* Dropdown Menu */}
              {showDropdown === event.id.toString() && (
                <div className="absolute right-2 sm:right-3 mt-1 w-36 sm:w-40 bg-white border border-gray-100 rounded-md shadow-md z-10">
                  <ul className="py-1 text-xs sm:text-sm">
                    <li>
                      <Link 
                        to={`/dashboard/events/${event.url}`}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-gray-700"
                      >
                        <Eye className="w-3 sm:w-3.5 h-3 sm:h-3.5" />
                        View Details
                      </Link>
                    </li>
                    <li>
                      <button 
                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-red-600 w-full text-left"
                        onClick={(e) => handleDeleteEvent(event, e)}
                        disabled={deletingEventIds[event.id]}
                      >
                        {deletingEventIds[event.id] ? (
                          <>
                            <Loader2 className="w-3 sm:w-3.5 h-3 sm:h-3.5 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-3 sm:w-3.5 h-3 sm:h-3.5" />
                            Delete
                          </>
                        )}
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-100 text-center">
            <div className="w-12 sm:w-16 h-12 sm:h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Calendar className="w-6 sm:w-8 h-6 sm:h-8 text-gray-300" />
            </div>
            <h3 className="text-sm sm:text-base font-medium mb-1">No events found</h3>
            <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
              {searchQuery 
                ? `No results found for "${searchQuery}"`
                : "You don't have any events in this category yet"}
            </p>
            <Link to="/dashboard/events/new">
              <Button className="h-8 sm:h-9 text-xs sm:text-sm">
                <Plus className="w-3 sm:w-3.5 h-3 sm:h-3.5 mr-1 sm:mr-1.5" />
                Create New Event
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {selectedEvent && (
        <QRCodeModal
          isOpen={!!selectedEvent}
          onClose={() => setSelectedEvent(null)}
          title={selectedEvent.title}
          eventUrl={selectedEvent.url}
          shareUrl={`https://ourphotos.co/e/${selectedEvent.url}`}
        />
      )}
    </div>
  );
};

export default EventList;